import { Card, Container, Icon, Colors, Typography, ButtonPrimary } from '@amelio/ui-library';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

import { useAmelioTranslations } from '@/hooks';
import { useNavigate } from '@/hooks';
import { RouteName } from '@/navigation/types';

export const SuccessMessageAction = {
    NAVIGATE_TO_EVAL_DASHBOARD: 'NAVIGATE_TO_EVAL_DASHBOARD',
    NAVIGATE_TO_CYCLE_EVAL_DASHBOARD: 'NAVIGATE_TO_CYCLE_EVAL_DASHBOARD',
    NAVIGATE_TO_HOME: 'NAVIGATE_TO_HOME',
    CLOSE_WINDOW: 'CLOSE_WINDOW',
} as const;

type SuccessMessageActionType = (typeof SuccessMessageAction)[keyof typeof SuccessMessageAction];

interface SuccessMessageLocationState {
    title: string;
    subtitle?: string;
    buttonLabel?: string;
    actionType?: SuccessMessageActionType;
}

/**
 * Success Message page - displays after successful evaluation creation
 */
export const SuccessMessagePage = () => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const location = useLocation<SuccessMessageLocationState>();
    const searchParams = new URLSearchParams(location.search);
    const actionParam = searchParams.get('action');

    const defaultStateByAction: Record<string, SuccessMessageLocationState> = {
        'close-preview': {
            title: t('reviewAnswer.successTitle'),
            subtitle: t('reviewAnswer.closeWindow'),
            buttonLabel: t('reviewAnswer.closePreview'),
            actionType: SuccessMessageAction.CLOSE_WINDOW,
        },
    };

    const fallbackState = actionParam ? defaultStateByAction[actionParam] : undefined;
    const resolvedState = location.state ?? fallbackState;

    if (!resolvedState) {
        return null;
    }

    const { title, subtitle, buttonLabel, actionType } = resolvedState;

    const handleAction = (action: SuccessMessageActionType) => {
        switch (action) {
            case SuccessMessageAction.NAVIGATE_TO_EVAL_DASHBOARD:
                navigate({ path: RouteName.Evaluation360 });
                break;
            case SuccessMessageAction.NAVIGATE_TO_CYCLE_EVAL_DASHBOARD:
                navigate({ path: RouteName.CycleEvaluation });
                break;
            case SuccessMessageAction.NAVIGATE_TO_HOME:
                window.location.href = '/';
                break;
            case SuccessMessageAction.CLOSE_WINDOW:
                window.close();
                break;
        }
    };

    return (
        <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className={'flex min-h-screen items-center justify-center'}
        >
            <Container p={'md'}>
                <div className={'relative max-w-2xl overflow-hidden'}>
                    {/* White glow effect - TWO POINTS: left and right */}
                    {/* Left blur */}
                    <div
                        className={
                            'absolute top-1/2 left-0 h-[200px] w-[400px] -translate-y-1/2 opacity-80 blur-lg'
                        }
                        style={{
                            background:
                                'radial-gradient(ellipse 60% 50% at 44% 30%, rgba(255,255,255,1) 40%, rgba(255,255,255,0.8) 30%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0) 100%)',
                        }}
                    />

                    {/* Right blur */}
                    <div
                        className={
                            'absolute top-1/2 right-0 h-[150px] w-[900px] -translate-y-1/2 opacity-100 blur-lg'
                        }
                        style={{
                            background:
                                'radial-gradient(ellipse 60% 50% at 80% 60%, rgba(255,255,255,1) 0%, rgba(255,255,255,0.8) 20%, rgba(255,255,255,0.3) 40%, rgba(255,255,255,0) 100%)',
                        }}
                    />

                    <Card
                        variant={'default'}
                        padding={'3xl'}
                        borderRadius={'2xl'}
                        borderColor={Colors.PRIMARY}
                        bgColor={'none'}
                        dataTestId={'success-message-page'}
                    >
                        <div className={'relative z-10 flex flex-col items-center gap-8 pb-6'}>
                            {/* Icon container - large circle with success background */}
                            <div
                                className={
                                    'flex h-22 w-22 items-center justify-center rounded-full'
                                }
                                style={{ backgroundColor: Colors.BASE_50 }}
                            >
                                <Icon
                                    name={'essential-checkmark'}
                                    size={50}
                                    color={Colors.PRIMARY}
                                    ariaLabel={'Success'}
                                />
                            </div>

                            {/* Message text */}
                            <header className={'flex w-full flex-col items-center gap-3'}>
                                <Typography
                                    align={'center'}
                                    variant={'h3'}
                                    color={Colors.BASE}
                                    className={'w-full text-center font-normal'}
                                >
                                    {title}
                                </Typography>

                                {subtitle && (
                                    <Typography
                                        align={'center'}
                                        variant={'body'}
                                        color={Colors.BASE_500}
                                        className={'w-full text-center'}
                                    >
                                        {subtitle}
                                    </Typography>
                                )}
                            </header>
                        </div>
                    </Card>

                    {buttonLabel && actionType && (
                        <Container
                            my={'lg'}
                            className={'flex justify-center'}
                        >
                            <ButtonPrimary onClick={() => handleAction(actionType)}>
                                <Icon
                                    name={'arrow-arrow-left'}
                                    color={Colors.WHITE}
                                    className={'mr-2'}
                                />

                                {buttonLabel}
                            </ButtonPrimary>
                        </Container>
                    )}
                </div>
            </Container>
        </motion.div>
    );
};
